import { useEffect, useState, useCallback } from "react";
import { STRINGS } from "@/lib/i18n";
import UI_CATALOG from "@/generated/ui_catalog.json";
import {
  trLanguages, trProgress, trList, trSaveRow, trBulkStatus, trExportUrl,
  trImportPreview, trImportCommit, trImportHistory, trImportErrorsUrl, trRegisterUi,
  trMigrationPreview, trMigrationRun, trMigrationVerify, trRemoveLegacy, trCollectUi,
} from "@/lib/api";
import { toast } from "sonner";
import {
  Languages, Download, Upload, Loader2, CheckCheck, Save, RefreshCw, Database,
  AlertTriangle, FileWarning, ClipboardCheck, Trash2, Search, ScanLine,
} from "lucide-react";

const STATUS_COLORS = {
  untranslated: "text-ink-3", draft: "text-gold", translated: "text-blue-600",
  needs_review: "text-orange-600", approved: "text-green-600", published: "text-green-700",
  outdated: "text-red-600",
};
const STATUSES = ["untranslated", "draft", "translated", "needs_review", "approved", "published", "outdated"];
const ENTITY_TYPES = ["content", "hub", "deity", "temple", "category", "festival", "post", "ui", "page", "seo"];

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "strings", label: "Strings" },
  { key: "io", label: "Export / Import" },
  { key: "migration", label: "Legacy Hindi Migration" },
];

export default function AdminTranslations() {
  const [langs, setLangs] = useState([]);
  const [lang, setLang] = useState("hi");
  const [tab, setTab] = useState("dashboard");
  const [rescanning, setRescanning] = useState(false);

  useEffect(() => {
    trLanguages().then((l) => setLangs(l.filter((x) => x.code !== "en"))).catch(() => {});
    // Seed UI-string sources (English + Hindi) into the centralized layer, once.
    if (!localStorage.getItem("dh_ui_registered_v1")) {
      trRegisterUi(STRINGS.en, STRINGS.hi)
        .then(() => localStorage.setItem("dh_ui_registered_v1", "1"))
        .catch(() => {});
    }
  }, []);

  const rescan = async () => {
    setRescanning(true);
    try {
      const r = await trCollectUi(UI_CATALOG);
      toast.success(
        r.added
          ? `Rescan complete — ${r.added} new string${r.added === 1 ? "" : "s"} added (${r.total} total).`
          : `Rescan complete — all ${r.total} UI strings already synced.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rescan failed.");
    } finally {
      setRescanning(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid="admin-translations">
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <Languages className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">Translations</h1>
        <button
          data-testid="tr-rescan-ui" onClick={rescan} disabled={rescanning}
          title={`Sync the ${UI_CATALOG.length} UI strings wrapped in <T> into the translation catalog`}
          className="ml-auto inline-flex items-center gap-1.5 border border-line px-3 py-2 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-50"
        >
          {rescanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanLine className="h-3.5 w-3.5" />}
          Rescan UI strings
        </button>
      </div>
      <p className="mb-6 text-sm text-ink-3">
        One source of truth for every language. Export a CSV, translate it externally, import it back,
        review and publish. English is always the fallback.
      </p>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <label className="text-sm text-ink-2">Language</label>
        <select
          data-testid="tr-lang-select" value={lang} onChange={(e) => setLang(e.target.value)}
          className="border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none"
        >
          {langs.map((l) => <option key={l.code} value={l.code}>{l.native} ({l.code})</option>)}
        </select>
      </div>

      <div className="mb-6 flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.key} data-testid={`tr-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm transition-colors ${tab === t.key ? "border-b-2 border-saffron text-saffron" : "text-ink-2 hover:text-ink"}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === "dashboard" && <Dashboard lang={lang} />}
      {tab === "strings" && <Strings lang={lang} />}
      {tab === "io" && <ExportImport lang={lang} />}
      {tab === "migration" && <Migration />}
    </div>
  );
}

function Dashboard({ lang }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    trProgress(lang).then((d) => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, [lang]);
  if (loading) return <Loader2 className="h-5 w-5 animate-spin text-saffron" />;
  if (!data) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="tr-dashboard">
      {data.entities.map((e) => (
        <div key={e.entity_type} data-testid={`tr-progress-${e.entity_type}`} className="border border-line bg-surface p-4">
          <div className="mb-1 flex items-center justify-between">
            <p className="font-medium text-ink">{e.label}</p>
            <span className="text-sm text-saffron">{e.percent}%</span>
          </div>
          <div className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-alt)]">
            <div className="h-full rounded-full bg-[var(--saffron)]" style={{ width: `${e.percent}%` }} />
          </div>
          <p className="text-xs text-ink-3">
            {e.total} fields · {e.published} published · {e.untranslated} untranslated
            {e.outdated ? <span className="text-red-600"> · {e.outdated} outdated</span> : null}
          </p>
        </div>
      ))}
    </div>
  );
}

function Strings({ lang }) {
  const [entityType, setEntityType] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    trList({ lang, entity_type: entityType, status, q, skip, limit: 100 })
      .then((d) => { setRows(d.rows); setTotal(d.total); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [lang, entityType, status, q, skip]);
  useEffect(() => { load(); }, [load]);

  const key = (r) => r.translation_key;
  const setEdit = (r, patch) => setEdits((e) => ({ ...e, [key(r)]: { ...(e[key(r)] || { translated_text: r.translated_text, status: r.status }), ...patch } }));
  const save = async (r) => {
    const e = edits[key(r)] || {};
    await trSaveRow({
      entity_type: r.entity_type, entity_id: r.entity_id, field: r.field, language: lang,
      source_text: r.source_text,
      translated_text: e.translated_text ?? r.translated_text,
      status: e.status ?? (r.status === "untranslated" ? "translated" : r.status),
    });
    toast.success("Saved");
    load();
  };

  const publishAllTranslated = async () => {
    const r = await trBulkStatus({ language: lang, entity_type: entityType || undefined, from_status: undefined, status: "published" });
    toast.success(`Published ${r.updated} translated entries`);
    load();
  };

  return (
    <div data-testid="tr-strings">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select data-testid="tr-filter-entity" value={entityType} onChange={(e) => { setSkip(0); setEntityType(e.target.value); }} className="border border-line bg-parchment px-3 py-2 text-sm text-ink">
          <option value="">All content types</option>
          {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select data-testid="tr-filter-status" value={status} onChange={(e) => { setSkip(0); setStatus(e.target.value); }} className="border border-line bg-parchment px-3 py-2 text-sm text-ink">
          <option value="">Any status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex items-center gap-1 border border-line bg-parchment px-2">
          <Search className="h-4 w-4 text-ink-3" />
          <input data-testid="tr-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search source or translation…" className="bg-transparent px-1 py-2 text-sm text-ink focus:outline-none" />
        </div>
        <button data-testid="tr-publish-all" onClick={publishAllTranslated} className="ml-auto inline-flex items-center gap-1.5 border border-line px-3 py-2 text-xs text-ink-2 hover:border-saffron hover:text-saffron">
          <CheckCheck className="h-3.5 w-3.5" /> Publish all translated
        </button>
      </div>

      <p className="mb-2 text-xs text-ink-3">{total} rows</p>
      {loading ? <Loader2 className="h-5 w-5 animate-spin text-saffron" /> : (
        <div className="space-y-2">
          {rows.map((r) => {
            const e = edits[key(r)] || {};
            return (
              <div key={key(r)} data-testid={`tr-row-${r.entity_id}-${r.field}`} className="border border-line bg-surface p-3">
                <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-ink-3">
                  <span className="rounded bg-[var(--surface-alt)] px-1.5 py-0.5">{r.entity_type}</span>
                  <span>{r.entity_id}</span><span>·</span><span>{r.field}</span>
                  <span className={`ml-auto uppercase ${STATUS_COLORS[e.status ?? r.status] || ""}`}>{e.status ?? r.status}</span>
                </div>
                <p className="mb-2 text-sm text-ink-2">{r.source_text}</p>
                <textarea
                  data-testid={`tr-input-${r.entity_id}-${r.field}`}
                  value={e.translated_text ?? r.translated_text} onChange={(ev) => setEdit(r, { translated_text: ev.target.value })}
                  rows={2} dir="auto" className="w-full border border-line bg-parchment px-2 py-1.5 text-sm text-ink focus:border-saffron focus:outline-none" />
                <div className="mt-2 flex items-center gap-2">
                  <select value={e.status ?? r.status} onChange={(ev) => setEdit(r, { status: ev.target.value })} className="border border-line bg-parchment px-2 py-1 text-xs text-ink">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button data-testid={`tr-save-${r.entity_id}-${r.field}`} onClick={() => save(r)} className="inline-flex items-center gap-1 bg-[var(--saffron)] px-3 py-1 text-xs text-white hover:bg-[var(--saffron-dark)]">
                    <Save className="h-3 w-3" /> Save
                  </button>
                </div>
              </div>
            );
          })}
          {rows.length === 0 && <p className="text-sm text-ink-3">No rows for this filter.</p>}
        </div>
      )}
      {total > 100 && (
        <div className="mt-4 flex items-center gap-2">
          <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - 100))} className="border border-line px-3 py-1.5 text-xs disabled:opacity-40">Previous</button>
          <span className="text-xs text-ink-3">{skip + 1}–{Math.min(skip + 100, total)}</span>
          <button disabled={skip + 100 >= total} onClick={() => setSkip(skip + 100)} className="border border-line px-3 py-1.5 text-xs disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

function ExportImport({ lang }) {
  const [entityType, setEntityType] = useState("");
  const [scope, setScope] = useState("all");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importId, setImportId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => trImportHistory().then(setHistory).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const doExport = () => { window.open(trExportUrl(lang, entityType, scope), "_blank"); };

  const doPreview = async () => {
    if (!file) { toast.error("Choose a CSV file first."); return; }
    setBusy(true);
    try {
      const r = await trImportPreview(file, lang);
      setPreview(r); setImportId(r.import_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed."); }
    finally { setBusy(false); }
  };
  const doCommit = async () => {
    setBusy(true);
    try {
      const r = await trImportCommit(importId, true);
      toast.success(`Imported ${r.applied} rows${r.skipped ? ` (${r.skipped} skipped)` : ""}`);
      setPreview(null); setImportId(null); setFile(null); loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="tr-io">
      {/* Export */}
      <div className="border border-line bg-surface p-5">
        <p className="overline-label mb-3 text-gold">Export CSV</p>
        <div className="flex flex-wrap items-center gap-2">
          <select value={entityType} onChange={(e) => setEntityType(e.target.value)} className="border border-line bg-parchment px-3 py-2 text-sm text-ink">
            <option value="">All content types</option>
            {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={scope} onChange={(e) => setScope(e.target.value)} className="border border-line bg-parchment px-3 py-2 text-sm text-ink">
            <option value="all">All</option>
            <option value="untranslated">Untranslated only</option>
            <option value="outdated">Outdated only</option>
          </select>
          <button data-testid="tr-export-btn" onClick={doExport} className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm text-white hover:bg-[var(--saffron-dark)]">
            <Download className="h-4 w-4" /> Download CSV
          </button>
        </div>
        <p className="mt-2 text-xs text-ink-3">UTF-8 with BOM — Indic scripts open correctly in Excel/Sheets.</p>
      </div>

      {/* Import */}
      <div className="border border-line bg-surface p-5">
        <p className="overline-label mb-3 text-gold">Import CSV</p>
        <input data-testid="tr-import-file" type="file" accept=".csv" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} className="text-sm text-ink-2" />
        <div className="mt-3 flex gap-2">
          <button data-testid="tr-import-preview" onClick={doPreview} disabled={busy || !file} className="inline-flex items-center gap-1.5 border border-line px-4 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Validate & preview
          </button>
        </div>
        {preview && (
          <div data-testid="tr-import-preview-result" className="mt-4 border border-line bg-parchment p-4">
            <div className="grid grid-cols-3 gap-3 text-center text-sm sm:grid-cols-6">
              <Stat label="New" value={preview.summary.new} />
              <Stat label="Updated" value={preview.summary.updated} />
              <Stat label="Unchanged" value={preview.summary.unchanged} />
              <Stat label="Invalid" value={preview.summary.invalid} color="text-red-600" />
              <Stat label="Conflicts" value={preview.summary.conflicts} color="text-orange-600" />
              <Stat label="Duplicates" value={preview.summary.duplicates} color="text-orange-600" />
            </div>
            {preview.error_count > 0 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-red-600">
                <FileWarning className="h-4 w-4" /> {preview.error_count} problem rows.
                <a href={trImportErrorsUrl(importId)} target="_blank" rel="noreferrer" className="underline">Download error CSV</a>
              </div>
            )}
            <button data-testid="tr-import-commit" onClick={doCommit} disabled={busy} className="mt-4 inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm text-white hover:bg-[var(--saffron-dark)] disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />} Import valid rows
            </button>
          </div>
        )}
      </div>

      {/* History */}
      <div className="border border-line bg-surface p-5">
        <p className="overline-label mb-3 text-gold">Import history</p>
        <div className="space-y-2">
          {history.map((h) => (
            <div key={h.import_id} className="flex flex-wrap items-center gap-2 border border-line px-3 py-2 text-xs text-ink-2">
              <span className="font-medium text-ink">{h.filename}</span>
              <span className="text-ink-3">{(h.created_at || "").slice(0, 16).replace("T", " ")}</span>
              <span className={h.status === "committed" ? "text-green-700" : "text-gold"}>{h.status}</span>
              {h.status === "committed" && <span>· {h.applied} applied</span>}
              <span className="ml-auto">{h.summary?.invalid ? <a href={trImportErrorsUrl(h.import_id)} target="_blank" rel="noreferrer" className="text-red-600 underline">errors</a> : null}</span>
            </div>
          ))}
          {history.length === 0 && <p className="text-sm text-ink-3">No imports yet.</p>}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <p className={`text-lg font-semibold ${color || "text-ink"}`}>{value}</p>
      <p className="text-[11px] uppercase tracking-wide text-ink-3">{label}</p>
    </div>
  );
}

function Migration() {
  const [preview, setPreview] = useState(null);
  const [verify, setVerify] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirmText, setConfirmText] = useState("");

  const doPreview = async () => { setBusy("preview"); try { setPreview(await trMigrationPreview()); } finally { setBusy(""); } };
  const doRun = async () => {
    if (!window.confirm("Migrate all legacy Hindi + embedded translations into the centralized layer? This never deletes anything.")) return;
    setBusy("run");
    try { const r = await trMigrationRun(); toast.success(`Migrated ${r.migrated} records`); setVerify(await trMigrationVerify()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Migration failed."); } finally { setBusy(""); }
  };
  const doVerify = async () => { setBusy("verify"); try { setVerify(await trMigrationVerify()); } finally { setBusy(""); } };
  const doRemove = async () => {
    if (confirmText !== "REMOVE LEGACY HINDI") { toast.error('Type the exact phrase to confirm.'); return; }
    if (!window.confirm("This removes ONLY the old Hindi fields from the CMS. Hindi will keep displaying from the centralized layer. Continue?")) return;
    setBusy("remove");
    try { const r = await trRemoveLegacy(confirmText); toast.success(`Removed legacy fields from ${r.removed.content + r.removed.hub + r.removed.flat} docs`); setConfirmText(""); setVerify(await trMigrationVerify()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Removal blocked."); } finally { setBusy(""); }
  };

  useEffect(() => { doPreview(); doVerify(); }, []);

  return (
    <div className="space-y-6" data-testid="tr-migration">
      <div className="border border-line bg-surface p-5">
        <p className="overline-label mb-2 text-gold">Step 1 · Scan & preview</p>
        <p className="mb-3 text-sm text-ink-3">Detects legacy Hindi fields (description_hi, title_hi, body_hi, points_hi, hindi_title, LangText .hi …) and embedded i18n translations across all content.</p>
        <button data-testid="tr-mig-preview" onClick={doPreview} disabled={busy === "preview"} className="inline-flex items-center gap-1.5 border border-line px-4 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron">
          {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-scan
        </button>
        {preview && (
          <p className="mt-3 text-sm text-ink-2">
            Found <strong>{preview.legacy_hi_found}</strong> legacy Hindi values and <strong>{preview.i18n_found}</strong> embedded translations
            across languages {Object.keys(preview.languages || {}).join(", ") || "—"}.
          </p>
        )}
      </div>

      <div className="border border-line bg-surface p-5">
        <p className="overline-label mb-2 text-gold">Step 2 · Migrate</p>
        <button data-testid="tr-mig-run" onClick={doRun} disabled={busy === "run"} className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm text-white hover:bg-[var(--saffron-dark)] disabled:opacity-50">
          {busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />} Run migration
        </button>
      </div>

      <div className="border border-line bg-surface p-5">
        <p className="overline-label mb-2 text-gold">Step 3 · Verify</p>
        <button data-testid="tr-mig-verify" onClick={doVerify} disabled={busy === "verify"} className="inline-flex items-center gap-1.5 border border-line px-4 py-2 text-sm text-ink-2 hover:border-saffron hover:text-saffron">
          {busy === "verify" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />} Verify
        </button>
        {verify && (
          <div className="mt-3 text-sm text-ink-2">
            <p>{verify.represented} / {verify.total_legacy} legacy Hindi values are safely represented in the centralized layer.</p>
            {verify.missing?.length > 0
              ? <p className="mt-1 flex items-center gap-1.5 text-orange-600"><AlertTriangle className="h-4 w-4" /> {verify.missing.length}+ not yet represented — run migration again.</p>
              : <p className="mt-1 flex items-center gap-1.5 text-green-700"><CheckCheck className="h-4 w-4" /> All represented — safe to remove legacy fields.</p>}
          </div>
        )}
      </div>

      <div className="border border-red-300 bg-[rgba(220,60,40,0.04)] p-5">
        <p className="overline-label mb-2 text-red-600">Step 4 · Remove legacy Hindi fields (danger)</p>
        <p className="mb-3 text-sm text-ink-3">
          Removes ONLY the old Hindi-specific CMS fields. The Hindi translations stay in the centralized layer and
          the site continues to display Hindi exactly as before. Only enabled when verification passes.
        </p>
        <input
          data-testid="tr-remove-confirm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Type: REMOVE LEGACY HINDI"
          className="mb-3 w-full max-w-sm border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-red-500 focus:outline-none" />
        <div>
          <button
            data-testid="tr-remove-legacy" onClick={doRemove}
            disabled={busy === "remove" || !verify || verify.missing?.length > 0}
            className="inline-flex items-center gap-1.5 bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-40">
            {busy === "remove" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Remove legacy Hindi fields
          </button>
        </div>
      </div>
    </div>
  );
}
